/**
 * MealTimingCoachTip — iter1094 → iter1095
 *
 * Малка chip показвана в Diary + MealLog headers. Тегли `/api/meal-timing/coach-tip`
 * при mount + при hour change (every 5 min). Ако response е warn/good →
 * highlight цвят. Toggle „Прочети още" разкрива actionable съвет.
 *
 * iter1095: поддържа props.mealType (breakfast|lunch|dinner|snack) за
 * по-специфични съвети (напр. „Обяд в овулаторна = 600-700 kcal е ОК").
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * @param {"breakfast"|"lunch"|"dinner"|"snack"|null|"auto"} [props.mealType]
 *   • "auto" (default) → derive from local hour (закуска <11, обяд <15,
 *     snack <18, вечеря <22, night >= 22)
 *   • explicit meal type → forced lookup
 *   • null → no meal type, fallback към period_of_day
 */
export default function MealTimingCoachTip({ mealType = "auto" } = {}) {
  const { API, user } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [tip, setTip] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const hour = new Date().getHours();
        // iter1095 — auto-derive meal_type от часа ако не е зададен.
        let effectiveMealType = mealType;
        if (mealType === "auto") {
          if (hour >= 22 || hour < 5) effectiveMealType = null;   // night — no meal
          else if (hour < 11) effectiveMealType = "breakfast";
          else if (hour < 15) effectiveMealType = "lunch";
          else if (hour < 18) effectiveMealType = "snack";
          else effectiveMealType = "dinner";
        }
        const params = new URLSearchParams({ hour: String(hour) });
        if (effectiveMealType) params.set("meal_type", effectiveMealType);
        const { data } = await axios.get(`${API}/meal-timing/coach-tip?${params.toString()}`);
        if (!cancelled) setTip(data);
      } catch { /* silent — coach е cosmetic */ }
    };
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [API, user, mealType]);

  if (!tip || !tip.hint_bg) return null;

  const colorMap = {
    good: isDark
      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
      : "bg-emerald-50 border-emerald-200 text-emerald-800",
    warn: isDark
      ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
      : "bg-amber-50 border-amber-200 text-amber-800",
    info: isDark
      ? "bg-slate-800/60 border-slate-700 text-slate-200"
      : "bg-slate-50 border-slate-200 text-slate-700",
  };
  const cls = colorMap[tip.severity] || colorMap.info;
  const actionable = bg ? tip.actionable_bg : tip.actionable_en;
  const hint = bg ? tip.hint_bg : tip.hint_en;

  return (
    <div
      data-testid="meal-timing-coach"
      className={`rounded-xl border p-3 my-2 ${cls}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0 text-xs sm:text-sm font-semibold">{hint}</div>
        {actionable && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="meal-timing-coach-toggle"
            className="shrink-0 opacity-80 hover:opacity-100"
            aria-label={expanded ? (bg ? "Скрий" : "Hide") : (bg ? "Прочети повече" : "Read more")}
          >
            {expanded
              ? <ChevronUp className="w-4 h-4" />
              : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
      </div>
      {expanded && actionable && (
        <div
          data-testid="meal-timing-coach-actionable"
          className="mt-1.5 text-[11px] sm:text-xs opacity-90 leading-relaxed"
        >
          💡 {actionable}
        </div>
      )}
    </div>
  );
}
